import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import type { EPrintType } from "../../../../Enum/EPrintType"
import type { IPrinterConfigurationDto } from "../../../../Objects/PrintAutomation/PrintAutomation"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleCheck, faCircleXmark, faFloppyDisk, faGear, faPrint } from "@fortawesome/free-solid-svg-icons"
import { GenericDropDown } from "../../../../components/Dropdown/Dropdown"

type Props = {
  printer: IPrinterConfigurationDto
  onChangeType: (printer: IPrinterConfigurationDto, printType: EPrintType) => void
  onSaveType: (printer: IPrinterConfigurationDto, printType: EPrintType) => void
  onOpenSettings: (printer: IPrinterConfigurationDto) => void
  onTestPrint: (printerId: number) => void
}

const PRINT_TYPE_OPTIONS: Array<{ value: EPrintType; label: string }> = [
  { value: "Kitchen", label: "Cozinha" },
  { value: "Delivery", label: "Delivery" },
  { value: "Bar", label: "Bar" },
  { value: "All", label: "Todos" },
]

export const PrinterConfigCard: React.FC<Props> = ({
  printer,
  onChangeType,
  onSaveType,
  onOpenSettings,
  onTestPrint,
}) => {
  const { t } = useTranslation(["pages"])

  const isDisabled = !printer.isAgentActive

  const [selectedType, setSelectedType] = useState<EPrintType>(printer.printType)

  useEffect(() => {
    setSelectedType(printer.printType)
  }, [printer.printType])

  return (
    <article className="rounded-lg border-2 border-orange-100 bg-white p-3 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <strong className="text-sm text-slate-800">{printer.displayName}</strong>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${
                printer.isAgentActive
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              <FontAwesomeIcon icon={printer.isAgentActive ? faCircleCheck : faCircleXmark} />
              {printer.isAgentActive
                ? t("label_print_automation_active_in_agent")
                : t("label_print_automation_inactive_in_agent")}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">{printer.printerName}</p>
        </div>
      </div>

      {printer.isEnabled && !isDisabled && (
        <div className="mt-3 flex flex-wrap gap-2 items-center justify-between">
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-100"
            onClick={() => printer.id && onTestPrint(printer.id)}
            disabled={!printer.id}
            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.printAutomation.components.printerConfigCard.button.printerIdOnTestPrintPrinterId"
          >
            <FontAwesomeIcon icon={faPrint} />
            {t("label_print_automation_test_print")}
          </button>

          <button
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onSaveType(printer, selectedType)}
            disabled={isDisabled}
            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.printAutomation.components.printerConfigCard.button.onSaveTypePrinterSelectedType"
          >
            <FontAwesomeIcon icon={faFloppyDisk} />
            {t("label_print_automation_save_type")}
          </button>

          <button
            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
            onClick={() => onOpenSettings(printer)}
            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.printAutomation.components.printerConfigCard.button.onOpenSettingsPrinter"
          >
            <FontAwesomeIcon icon={faGear} />
            {t("label_print_automation_printer_settings")}
          </button>

          {printer.isEnabled && !isDisabled && (
            <div className="flex w-full flex-col sm:w-auto sm:min-w-[220px]">
              <GenericDropDown
                initialState={selectedType}
                data={PRINT_TYPE_OPTIONS}
                disable={isDisabled}
                handlerFuncion={(event) => {
                  const nextType = event.target.value as EPrintType
                  setSelectedType(nextType)
                  onChangeType(printer, nextType)
                }}
                descriptionKey="interactionDescriptions:pages.myRestaurant.printAutomation.components.printerConfigCard.select.select"
              />
            </div>
          )}
        </div>
      )}
    </article>
  )
}
